import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useEntitlement } from '../hooks/useEntitlement';
import { EntitlementErrorBoundary } from '../components/EntitlementErrorBoundary';

interface PremiumStatus {
  isPremium: boolean;
  isCancelling: boolean;
  subscriptionEndDate: string | null;
  planType: string | null;
  isStudent: boolean;
  isCorporate: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

const PremiumContext = createContext<PremiumStatus | undefined>(undefined);

/**
 * Internal component that uses useEntitlement hook
 */
const PremiumProviderInternal: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { is_premium, is_cancelling, subscription_end_date, plan_type, is_student, is_corporate, loading, error, refetch } = useEntitlement();

  // Convert useEntitlement refetch to async function for backward compatibility
  const refetchAsync = useCallback(async () => {
    refetch();
  }, [refetch]);

  // If there's an error, throw it so the error boundary can catch it
  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const value: PremiumStatus = {
    isPremium: is_premium,
    isCancelling: is_cancelling,
    subscriptionEndDate: subscription_end_date,
    planType: plan_type,
    isStudent: is_student,
    isCorporate: is_corporate,
    loading,
    refetch: refetchAsync
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};

/**
 * PremiumProvider with error boundary for entitlement loading failures
 * Uses the new useEntitlement hook internally while maintaining backward compatibility
 */
export const PremiumProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <EntitlementErrorBoundary
      fallback={(error, resetError) => (
        <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Premium status unavailable
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                Unable to load subscription status. The app will continue with free tier access.
              </p>
              <button
                onClick={resetError}
                className="mt-3 text-sm font-medium text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 underline"
              >
                Retry loading premium status
              </button>
            </div>
          </div>
          {/* Render children with fallback premium status */}
          <PremiumContext.Provider
            value={{
              isPremium: false,
              isCancelling: false,
              subscriptionEndDate: null,
              planType: null,
              isStudent: false,
              isCorporate: false,
              loading: false,
              refetch: async () => {
                resetError();
              }
            }}
          >
            {children}
          </PremiumContext.Provider>
        </div>
      )}
    >
      <PremiumProviderInternal>
        {children}
      </PremiumProviderInternal>
    </EntitlementErrorBoundary>
  );
};

export const usePremiumContext = () => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremiumContext must be used within PremiumProvider');
  }
  return context;
};
